/**
 * Input - Reusable input component
 * Wrapper around Material-UI TextField with consistent styling
 */

import React from 'react';
import {
  TextField,
  TextFieldProps,
  InputAdornment,
  IconButton
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface InputProps extends Omit<TextFieldProps, 'variant'> {
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  showPasswordToggle?: boolean;
}

export const Input = React.forwardRef<HTMLDivElement, InputProps>(
  ({
    icon,
    iconPosition = 'start',
    showPasswordToggle = false,
    type: initialType,
    ...props
  }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);

    const type = showPasswordToggle && showPassword ? 'text' : initialType;

    const startAdornment = icon && iconPosition === 'start' ? (
      <InputAdornment position="start">{icon}</InputAdornment>
    ) : undefined;

    const endAdornment = showPasswordToggle ? (
      <InputAdornment position="end">
        <IconButton
          onClick={() => setShowPassword(!showPassword)}
          edge="end"
          size="small"
        >
          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </InputAdornment>
    ) : icon && iconPosition === 'end' ? (
      <InputAdornment position="end">{icon}</InputAdornment>
    ) : undefined;

    return (
      <TextField
        ref={ref}
        {...props}
        type={type}
        variant="outlined"
        fullWidth
        InputProps={{
          startAdornment,
          endAdornment,
          ...props.InputProps
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            '&:hover fieldset': {
              borderColor: '#14B8A6'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#14B8A6'
            }
          },
          ...props.sx
        }}
      />
    );
  }
);

Input.displayName = 'Input';

export default Input;
